/**
 * Release Board data: one infinite (cursor paged) query per column plus the
 * per-column totals. All of them share the same filters.
 *
 * No auto retry loops: queries use the app default retry; failures surface a
 * per-column manual retry button.
 */

import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { approvalService } from '@/services/approval.service';
import { releaseSectionsService } from '@/services/releaseSections.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import {
  type BoardFacets,
  type BoardFilters,
  type BoardItem,
  type ReleaseBoardColumnKey,
  type ReleaseBoardCounts,
} from '@/types/releaseBoard';

export const RELEASE_BOARD_PAGE_SIZE = 20;
const COLUMN_POLL_MS = 60_000;
const COUNTS_POLL_MS = 30_000;

/** Queries are `enabled` only with a team; this satisfies the type checker without `!`. */
function requireTeamId(teamId: number | null): number {
  if (teamId === null) throw new Error('No team selected');
  return teamId;
}

/**
 * Where the Release Board can exist at all:
 * - `loading`: the server has not yet said whether the brand runs the release workflow
 * - `no_brand`: the user has no team at all (e.g. a super admin outside God Mode): pick a brand first
 * - `standalone`: the brand is not part of an agency, so there is no release workflow
 * - `workflow_off`: agency brand with carte blanche ON (no release needed)
 * - `active`: the brand runs the release workflow
 *
 * workflow on/off comes from the SERVER (`release-badges.workflowActive`, refreshed by
 * invalidateRelease), not from the /me snapshot, which goes stale after a carte-blanche toggle.
 */
export type ReleaseBoardAvailability = 'loading' | 'no_brand' | 'standalone' | 'workflow_off' | 'active';

export interface ReleaseBoardGate {
  availability: ReleaseBoardAvailability;
  /** Board queries run only when active; every other state renders its own screen */
  enabled: boolean;
  teamId: number | null;
}

export function useReleaseBoardGate(): ReleaseBoardGate {
  const { teamId, hasTeam } = useEffectiveTeamId();
  const { isAgencyTeam, carteBlanche } = useReviewerRestrictions();
  // Same key as the nav badges query, so invalidateRelease refreshes both.
  const workflow = useQuery({
    queryKey: teamQueryKeys.approval.board.badges(teamId),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => releaseSectionsService.getBadges(requireTeamId(teamId)),
    enabled: hasTeam && teamId !== null,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  let availability: ReleaseBoardAvailability;
  if (!hasTeam) availability = 'no_brand';
  else if (workflow.data) availability = workflow.data.workflowActive ? 'active' : isAgencyTeam ? 'workflow_off' : 'standalone';
  else if (workflow.isError) {
    // Server unreachable: fall back to the /me snapshot rather than hanging on a skeleton
    availability = !isAgencyTeam ? 'standalone' : carteBlanche ? 'workflow_off' : 'active';
  } else availability = 'loading';
  return { availability, enabled: availability === 'active', teamId };
}

export interface BoardColumnState {
  /** All loaded items (de-duplicated across pages) */
  items: BoardItem[];
  /** Total matching items in this column (from the first page) */
  total: number | null;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

function useBoardColumn(column: ReleaseBoardColumnKey, filters: BoardFilters): BoardColumnState {
  const { teamId, enabled } = useReleaseBoardGate();

  const query = useInfiniteQuery({
    queryKey: teamQueryKeys.approval.board.column(teamId, column, filters),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: ({ pageParam }) =>
      approvalService.getReleaseBoardPage(requireTeamId(teamId), {
        ...filters,
        column,
        cursor: pageParam,
        limit: RELEASE_BOARD_PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    refetchInterval: COLUMN_POLL_MS,
    // No placeholderData: when the filter key changes the old (differently filtered) cards must not
    // stay on screen, or a failing request would silently leave them showing as if filtered.
    // Same key (polling / paging / refetch) keeps its data natively.
  });

  const pages = query.data?.pages;
  const items = useMemo<BoardItem[]>(() => {
    const seen = new Set<string>();
    const merged: BoardItem[] = [];
    for (const page of pages ?? []) {
      for (const item of page.items) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        merged.push(item);
      }
    }
    return merged;
  }, [pages]);

  const { fetchNextPage, refetch } = query;
  return {
    items,
    total: pages?.[0]?.total ?? null,
    // A disabled query is 'pending' forever in TanStack v5: only an enabled one is loading
    isLoading: enabled && query.isPending,
    isError: query.isError && !query.data,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => void fetchNextPage(),
    refetch: () => void refetch(),
  };
}

export interface ReleaseBoardState {
  columns: Record<ReleaseBoardColumnKey, BoardColumnState>;
  counts: ReleaseBoardCounts | undefined;
  /** Every loaded item across all columns */
  allItems: BoardItem[];
}

/** The backend ignores searches shorter than 2 characters (400); normalise so keys stay stable. */
const MIN_SEARCH_LENGTH = 2;

export function useReleaseBoard(inputFilters: BoardFilters): ReleaseBoardState {
  const { teamId, enabled } = useReleaseBoardGate();
  const filters = useMemo<BoardFilters>(() => {
    const q = inputFilters.q.trim();
    return { ...inputFilters, q: q.length >= MIN_SEARCH_LENGTH ? q : '' };
  }, [inputFilters]);

  const inReview = useBoardColumn('in_review', filters);
  const changesRequested = useBoardColumn('changes_requested', filters);
  const released = useBoardColumn('released', filters);
  const scheduled = useBoardColumn('scheduled', filters);

  const countsQuery = useQuery({
    queryKey: teamQueryKeys.approval.board.counts(teamId, filters),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => approvalService.getReleaseBoardCounts(requireTeamId(teamId), filters),
    enabled,
    refetchInterval: COUNTS_POLL_MS,
  });

  const columns: Record<ReleaseBoardColumnKey, BoardColumnState> = {
    in_review: inReview,
    changes_requested: changesRequested,
    released,
    scheduled,
  };

  const allItems = useMemo<BoardItem[]>(
    () => [
      ...inReview.items,
      ...changesRequested.items,
      ...released.items,
      ...scheduled.items,
    ],
    [inReview.items, changesRequested.items, released.items, scheduled.items]
  );

  return { columns, counts: countsQuery.data, allItems };
}

/**
 * Filter facets of the board: the single source for which filter menus exist and what they offer
 * (creators / labels / languages / media kinds / versions / needed-by of the items actually on this board).
 */
export function useBoardFacets(): { facets: BoardFacets | undefined; isLoading: boolean } {
  const { teamId, enabled } = useReleaseBoardGate();
  const query = useQuery({
    queryKey: teamQueryKeys.approval.board.facets(teamId),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => approvalService.getReleaseBoardFacets(requireTeamId(teamId)),
    enabled,
    staleTime: 60_000,
  });
  return { facets: query.data, isLoading: enabled && query.isPending };
}
